<template>
  <div class="after-sales-travel">
    <div class="after-sales-travel__header">
      <template v-if="showCheckbox">
        <Checkbox
          v-if="hasCancellableProducts"
          :id="'segment-' + segment.id"
          :model-value="isChecked"
          :disabled="disabled"
          @update:modelValue="handleSegmentChange"
          class="after-sales-travel__header-checkbox"
          additionalClass="checkbox--red"
        >
          <div class="after-sales-travel__header-content">
            <div class="after-sales-travel__header-top">
              <span
                v-if="directionLabel"
                class="after-sales-travel__direction"
              >
                {{ directionLabel }}
              </span>
              <span class="after-sales-travel__datetime">
                {{
                  formatDateShort(
                    segment.booking_journey_segments[0].departure_date_time
                  )
                }}
                |
                {{
                  formatTime(
                    segment.booking_journey_segments[0].departure_date_time
                  )
                }}-{{
                  formatTime(
                    segment.booking_journey_segments[
                      segment.booking_journey_segments.length - 1
                    ].arrival_date_time
                  )
                }}
              </span>
            </div>
            <div class="after-sales-travel__route">
              <span class="after-sales-travel__station">
                {{ getOriginStation(segment) }}
              </span>
              <span class="after-sales-travel__arrow">&rarr;</span>
              <span class="after-sales-travel__station">
                {{ getFinalDestination(segment) }}
              </span>
            </div>
          </div>
        </Checkbox>
        <div
          v-else
          class="after-sales-travel__header-content after-sales-travel__header-content--no-cancel"
        >
          <div class="after-sales-travel__header-top">
            <span
              v-if="directionLabel"
              class="after-sales-travel__direction"
            >
              {{ directionLabel }}
            </span>
            <span class="after-sales-travel__datetime">
              {{
                formatDateShort(
                  segment.booking_journey_segments[0].departure_date_time
                )
              }}
              |
              {{
                formatTime(
                  segment.booking_journey_segments[0].departure_date_time
                )
              }}-{{
                formatTime(
                  segment.booking_journey_segments[0].arrival_date_time
                )
              }}
            </span>
          </div>
          <div class="after-sales-travel__route">
            <span class="after-sales-travel__station">
              {{ getOriginStation(segment) }}
            </span>
            <span class="after-sales-travel__arrow">&rarr;</span>
            <span class="after-sales-travel__station">
              {{ getFinalDestination(segment) }}
            </span>
          </div>
          <div class="after-sales-travel__no-cancel-message">
            Denne delen av reisen kan ikke kanselleres
          </div>
        </div>
      </template>
      <template v-else>
        <div class="after-sales-travel__header-content">
          <div class="after-sales-travel__header-top">
            <span
              v-if="directionLabel"
              class="after-sales-travel__direction"
            >
              {{ directionLabel }}
            </span>
            <span class="after-sales-travel__datetime">
              {{
                formatDateShort(
                  segment.booking_journey_segments[0].departure_date_time
                )
              }}
              |
              {{
                formatTime(
                  segment.booking_journey_segments[0].departure_date_time
                )
              }}-{{
                formatTime(
                  segment.booking_journey_segments[0].arrival_date_time
                )
              }}
            </span>
          </div>
          <div class="after-sales-travel__route">
            <span class="after-sales-travel__station">
              {{ getOriginStation(segment) }}
            </span>
            <span class="after-sales-travel__arrow">&rarr;</span>
            <span class="after-sales-travel__station">
              {{ getFinalDestination(segment) }}
            </span>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="hasPassengers && !isRebooking"
      class="after-sales-travel__passengers"
    >
      <div
        v-for="product in filteredProducts"
        :key="product.uuid"
        class="after-sales-travel__passenger"
        :class="{
          'after-sales-travel__passenger--non-cancellable':
            !canCancelProduct(product),
        }"
      >
        <template v-if="showCheckbox && canCancelProduct(product)">
          <Checkbox
            v-if="hasMultiplePassengers"
            :id="product.uuid"
            v-model="selectedPassengers[product.uuid]"
            :disabled="disabled"
            @update:modelValue="
              (value) => handlePassengerChange(product.uuid, value)
            "
            class="after-sales-travel__passenger-checkbox"
            additionalClass="checkbox--red"
          >
            <div class="after-sales-travel__passenger-content">
              <div class="after-sales-travel__passenger-main">
                <div>
                  <span class="after-sales-travel__passenger-name">
                    {{ getPassengerName(product.passenger_id) }}
                  </span>
                  <div class="after-sales-travel__passenger-details">
                    <span class="after-sales-travel__passenger-type">
                      {{ product.name }}
                    </span>
                    <span
                      v-if="product.seat"
                      class="after-sales-travel__passenger-seat"
                    >
                      Sete: {{ product.seat.number }}
                    </span>
                    <span
                      v-for="prop in product.seat_properties"
                      :key="prop.code"
                      class="after-sales-travel__passenger-property"
                    >
                      {{ prop.description }}
                    </span>
                  </div>
                </div>
                <span class="after-sales-travel__passenger-price">
                  {{ formatPrice(getPassengerTotalPrice(product), false) }}
                </span>
              </div>
            </div>
          </Checkbox>
          <div
            v-else
            class="after-sales-travel__passenger-content"
          >
            <div class="after-sales-travel__passenger-main">
              <span class="after-sales-travel__passenger-name">
                {{ getPassengerName(product.passenger_id) }}
              </span>
              <span class="after-sales-travel__passenger-price">
                {{ formatPrice(getPassengerTotalPrice(product), false) }}
              </span>
            </div>
            <div class="after-sales-travel__passenger-details">
              <span class="after-sales-travel__passenger-type">
                {{ product.name }}
              </span>
              <span
                v-if="product.seat"
                class="after-sales-travel__passenger-seat"
              >
                Sete: {{ product.seat.number }}
              </span>
              <span
                v-for="prop in product.seat_properties"
                :key="prop.code"
                class="after-sales-travel__passenger-property"
              >
                {{ prop.description }}
              </span>
              <!-- Equipment Display -->
              <template v-if="getPassengerEquipment(product)?.length">
                <span
                  v-for="equipment in getPassengerEquipment(product)"
                  :key="equipment.uuid"
                  class="after-sales-travel__passenger-property after-sales-travel__passenger-property--equipment"
                >
                  {{
                    t(
                      `descriptions.equipmentTypes.${
                        equipment.product_code || equipment.code
                      }.displayName`
                    )
                  }}
                </span>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="after-sales-travel__passenger-content">
            <div class="after-sales-travel__passenger-main">
              <span class="after-sales-travel__passenger-name">
                {{ getPassengerName(product.passenger_id) }}
              </span>
              <span class="after-sales-travel__passenger-price">
                {{ formatPrice(getPassengerTotalPrice(product), false) }}
              </span>
            </div>
            <div class="after-sales-travel__passenger-details">
              <span class="after-sales-travel__passenger-type">
                {{ product.name }}
              </span>
              <span
                v-if="product.seat"
                class="after-sales-travel__passenger-seat"
              >
                Sete: {{ product.seat.number }}
              </span>
              <span
                v-for="prop in product.seat_properties"
                :key="prop.code"
                class="after-sales-travel__passenger-property"
              >
                {{ prop.description }}
              </span>
              <template v-if="getPassengerEquipment(product)?.length">
                <span
                  v-for="equipment in getPassengerEquipment(product)"
                  :key="equipment.uuid"
                  class="after-sales-travel__passenger-property after-sales-travel__passenger-property--equipment"
                >
                  {{
                    t(
                      `descriptions.equipmentTypes.${
                        equipment.product_code || equipment.code
                      }.displayName`
                    )
                  }}
                </span>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import Checkbox from '@/components/BaseComponents/Checkbox.vue'
  import { formatDateShort, formatTime, formatPrice } from '@/utils/formatters'
  import { BookingTariffSegment, RequiredProduct } from '@/types/bookingTypes'
  import { TravelSeat } from '@/types/seatTypes'

  interface ProductWithSeat extends RequiredProduct {
    seat?: {
      number: string
    }

    seat_properties: Array<{
      code: string
      description: string
    }>
  }

  interface Props {
    segment: BookingTariffSegment
    directionLabel?: string
    showCheckbox?: boolean
    disabled?: boolean
    selectedPassengers?: Record<string, boolean>
    hasPassengers?: boolean
    isRebooking?: boolean
    isChecked?: boolean
    passengers?: any[]
  }

  const { t } = useI18n()

  const props = withDefaults(defineProps<Props>(), {
    directionLabel: '',
    showCheckbox: false,
    disabled: false,
    selectedPassengers: () => ({}),
    hasPassengers: true,
    isRebooking: false,
    isChecked: false,
  })

  if (!props.segment) {
    throw new Error('AfterSalesTravel: segment prop is required')
  }

  const emit = defineEmits<{
    (e: 'segment-change', segmentId: string, value: boolean): void
    (e: 'passenger-change', passengerId: string, value: boolean): void
    (e: 'update:selectedPassengers', value: Record<string, boolean>): void
  }>()

  const getPassengerName = (passengerId: string) => {
    const passenger = props.passengers?.find((p) => p.id === passengerId)
    return passenger ? `${passenger.first_name} ${passenger.last_name}` : ''
  }

  const canCancelProduct = (product: ProductWithSeat): boolean => {
    if (!product.can_be_cancelled || !product.after_sales_rules) {
      return false
    }
    return product.after_sales_rules.some(
      (rule: any) =>
        rule.type === 'CANCEL' &&
        rule.active &&
        new Date(rule.valid_until) > new Date()
    )
  }

  const hasMultiplePassengers = computed(() => {
    const uniquePassengerIds = new Set(
      props.segment.required_products?.map(
        (p: ProductWithSeat) => p.passenger_id
      )
    )
    return uniquePassengerIds.size > 1
  })

  const hasCancellableProducts = computed(() => {
    return props.segment.required_products?.some(canCancelProduct) ?? false
  })

  const canRebookProduct = (product: ProductWithSeat): boolean => {
    return product.can_be_rebooked ?? false
  }

  const hasRebookableProducts = computed(() => {
    return props.segment.required_products?.some(canRebookProduct)
  })

  const getOriginStation = (segment: any) => {
    if (segment?.segments?.length) {
      return segment.segments[0].departure_station.name
    }
    return segment?.departure_station?.name || ''
  }

  const getFinalDestination = (segment: any) => {
    if (segment?.segments?.length) {
      return segment.segments[segment.segments.length - 1].arrival_station.name
    }
    return segment?.arrival_station?.name || ''
  }

  const getPassengerTotalPrice = (mainProduct: ProductWithSeat) => {
    let total = mainProduct.price || 0

    const additionalProducts =
      props.segment.additional_products?.filter(
        (product) => product.passenger_id === mainProduct.passenger_id
      ) || []

    additionalProducts.forEach((product) => {
      total += product.price || 0
    })

    return total
  }

  const getPassengerEquipment = (mainProduct: ProductWithSeat) => {
    return (
      props.segment.additional_products?.filter(
        (product) =>
          product.passenger_id === mainProduct.passenger_id &&
          ['LUGGAGE', 'SPECIAL_LUGGAGE', 'BIKE', 'SCOOTER', 'PET'].includes(
            product.code
          )
      ) || []
    )
  }

  const filteredProducts = computed<ProductWithSeat[]>(() => {
    if (props.showCheckbox) {
      return props.segment.required_products || []
    } else {
      return (props.segment.required_products || []).filter(
        (product: ProductWithSeat) => props.selectedPassengers[product.uuid]
      )
    }
  })

  watch(
    () => props.isChecked,
    (newValue) => {
      if (hasMultiplePassengers.value && props.segment.required_products) {
        props.segment.required_products.forEach((product: ProductWithSeat) => {
          if (canCancelProduct(product)) {
            props.selectedPassengers[product.uuid] = newValue
            emit('passenger-change', product.uuid, newValue)
          }
        })
        emit('update:selectedPassengers', props.selectedPassengers)
      }
    }
  )

  const handleSegmentChange = (value: boolean) => {
    emit('segment-change', props.segment.id, value)
  }

  const handlePassengerChange = (productId: string, value: boolean) => {
    emit('passenger-change', productId, value)
  }
</script>

<style lang="scss" scoped>
  .after-sales-travel {
    &__header {
      padding: rem(24) 24px;
      background-color: white;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      border-bottom: 1px solid $clr-primary-50;

      &-checkbox {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      &-content {
        flex-grow: 1;
      }

      &-content--no-cancel {
        padding: rem(16) 16px;
        background-color: #f5f5f5;
        border-radius: 4px;
      }

      &-top {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 0.5rem;

        @media screen and (max-width: $breakpoint-xxs) {
          font-size: rem(14);
        }
      }
    }

    &__direction {
      font-weight: 500;
      color: $clr-text;
    }

    &__route {
      display: flex;
      align-items: center;
      flex-flow: wrap;
      column-gap: 8px;
    }

    &__station {
      @include headline-small;
      font-size: rem(18);
    }

    &__arrow {
      color: $clr-secondary;
    }

    &__no-cancel-message {
      margin-top: 0.5rem;
      color: #666;
      font-size: 0.875rem;
    }

    &__passengers {
      padding: rem(16) 0;
    }

    &__passenger {
      margin-right: 24px;
      margin-left: 24px;
      padding-bottom: rem(16);
      margin-bottom: 0.75rem;
      border-bottom: 1px solid $clr-primary-100;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }

      &--non-cancellable {
        opacity: 0.7;
      }

      &-content {
        flex-grow: 1;
      }

      &-main {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
      }

      &-name {
        font-weight: 600;
      }

      &-details {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        font-size: 0.9rem;

        > * {
          &:not(:last-child)::after {
            content: '•';
            margin-left: 0.5rem;
          }
        }
      }

      &-checkbox {
        gap: 8px;
      }
    }
  }
</style>
